import {
  BucketIcon,
  CloudIcon,
  BrowserIcon,
  SourceCodeIcon,
  Globe02Icon,
  HierarchyIcon,
  InternetIcon,
  Plug01Icon,
  Route01Icon,
  ServerStack01Icon,
  SecurityCheckIcon,
  ShieldEnergyIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import { token } from '@terra/ui-styled-system';
import { AssetRelationshipTypeSchema } from '@terra/shared-contracts-external-network';
import type {
  AssetGraphNodeDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import type { AssetRelationshipType } from '@terra/shared-contracts-external-network';
import type { GraphNodeExpansion } from './resolveGraphNodeExpansion';

export type SyntheticGraphNodeType =
  | 'INTERNET'
  | 'CONNECTOR'
  | 'CLOUD_ACCOUNT'
  | 'APPLICATION'
  | 'WEAKNESS';
export type GraphNodeType = ExternalNetworkEntityKind | SyntheticGraphNodeType;
export const GRAPH_NODE_CIRCLE_SIZE = 48;
export const GRAPH_NODE_ICON: Record<GraphNodeType, TerraIcon> = {
  INTERNET: InternetIcon,
  CONNECTOR: HierarchyIcon,
  CLOUD_ACCOUNT: CloudIcon,
  APPLICATION: BrowserIcon,
  NETBLOCK: Route01Icon,
  DOMAIN: Globe02Icon,
  HOST: ServerStack01Icon,
  GATEWAY: SecurityCheckIcon,
  NETWORK_SERVICE: Plug01Icon,
  SOFTWARE: SourceCodeIcon,
  STORAGE_SERVICE: BucketIcon,
  WEAKNESS: ShieldEnergyIcon,
};
export const GRAPH_NODE_COLOR: Record<GraphNodeType, string> = {
  INTERNET: token('colors.purple.9'),
  CONNECTOR: token('colors.purple.9'),
  CLOUD_ACCOUNT: token('colors.gray.9'),
  APPLICATION: token('colors.blue.9'),
  NETBLOCK: token('colors.purple.9'),
  DOMAIN: token('colors.fg.accent.default'),
  HOST: token('colors.gray.9'),
  GATEWAY: token('colors.orange.9'),
  NETWORK_SERVICE: token('colors.green.9'),
  SOFTWARE: token('colors.orange.9'),
  STORAGE_SERVICE: token('colors.yellow.9'),
  WEAKNESS: token('colors.bg.graphs.pink'),
};
export const GRAPH_NODE_COLOR_PALETTE: Record<GraphNodeType, string> = {
  INTERNET: 'purple',
  CONNECTOR: 'purple',
  CLOUD_ACCOUNT: 'gray',
  APPLICATION: 'blue',
  NETBLOCK: 'purple',
  DOMAIN: 'blue',
  HOST: 'gray',
  GATEWAY: 'orange',
  NETWORK_SERVICE: 'green',
  SOFTWARE: 'orange',
  STORAGE_SERVICE: 'yellow',
  WEAKNESS: 'pink',
};
export type ExternalNetworkGraphLayout = 'standard' | 'sequential' | 'radial';
export type AssetNodeData = {
  label: string;
  entityKind: GraphNodeType;
  nodeType?: 'asset' | 'cluster' | 'weakness';
  signalsCount: number;
  scope?: string;
  cidr?: string;
  fqdn?: string;
  ipAddress?: string;
  port?: number;
  openPorts?: string[];
  transportProtocol?: string;
  applicationProtocol?: string;
  softwareType?: string;
  vendor?: string;
  product?: string;
  version?: string | null;
  cloudProvider?: string | null;
  cloudAccountRef?: string | null;
  publicAccess?: AssetGraphNodeDto['properties']['publicAccess'];
  websiteEnabled?: boolean;
  hostname?: string;
  cveCount?: number;
  applicationId?: string;
  isDraft?: boolean;
  isActionTarget?: boolean;
  isExpanding?: boolean;
  expansion?: GraphNodeExpansion;
};
export const ASSET_GRAPH_EDGE_TRANSLATION_KEY: Record<
  Exclude<AssetRelationshipType, 'HAS_WEAKNESS'>,
  `graph.edge.${Exclude<AssetRelationshipType, 'HAS_WEAKNESS'>}`
> = {
  WITHIN_NETBLOCK: 'graph.edge.WITHIN_NETBLOCK',
  IS_SUBDOMAIN_OF: 'graph.edge.IS_SUBDOMAIN_OF',
  RESOLVES_TO: 'graph.edge.RESOLVES_TO',
  FRONTED_BY: 'graph.edge.FRONTED_BY',
  SERVES_VHOST: 'graph.edge.SERVES_VHOST',
  ORIGIN_OF: 'graph.edge.ORIGIN_OF',
  RUNS_SERVICE: 'graph.edge.RUNS_SERVICE',
  RUNS_SOFTWARE: 'graph.edge.RUNS_SOFTWARE',
  HOSTS_APPLICATION: 'graph.edge.HOSTS_APPLICATION',
};
export const SYNTHETIC_EDGE_TYPES = {
  EXPOSES: 'EXPOSES',
  REACHES: 'REACHES',
  IN_CLOUD_ACCOUNT: 'IN_CLOUD_ACCOUNT',
} as const;
export type SyntheticEdgeType =
  (typeof SYNTHETIC_EDGE_TYPES)[keyof typeof SYNTHETIC_EDGE_TYPES];
export const SYNTHETIC_EDGE_TRANSLATION_KEY: Record<
  SyntheticEdgeType,
  `graph.edge.${SyntheticEdgeType}`
> = {
  EXPOSES: 'graph.edge.EXPOSES',
  REACHES: 'graph.edge.REACHES',
  IN_CLOUD_ACCOUNT: 'graph.edge.IN_CLOUD_ACCOUNT',
};
export type GraphEdgeTranslationKey =
  | (typeof ASSET_GRAPH_EDGE_TRANSLATION_KEY)[Exclude<
      AssetRelationshipType,
      'HAS_WEAKNESS'
    >]
  | (typeof SYNTHETIC_EDGE_TRANSLATION_KEY)[SyntheticEdgeType];
export const getGraphEdgeTranslationKey = (
  edgeType: string,
): GraphEdgeTranslationKey | null => {
  const parsed = AssetRelationshipTypeSchema.safeParse(edgeType);

  if (parsed.success) {
    if (parsed.data === 'HAS_WEAKNESS') {
      return null;
    }

    return ASSET_GRAPH_EDGE_TRANSLATION_KEY[parsed.data];
  }

  return SYNTHETIC_EDGE_TRANSLATION_KEY[edgeType as SyntheticEdgeType] ?? null;
};
